import { ReactNode, useCallback, useEffect } from 'react';
import Vec2 from 'vec2';

import { DrawingShapeContext } from './drawingShape.context';
import { DrawingShapeContextType } from './drawingShape.types';
import { useDrawingShape } from './hooks';

import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { autoPlaceTranslation } from '~/core/helpers';
import { DrawingShape } from '~/core/models';
import { getApproximateApplianceOutline } from '~/core/utils/appliance.utils';
import { useAppDispatch } from '~/redux/hooks';
import {
  resetActiveDrawingObject,
  setActiveDrawingObject,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';

export const DrawingShapeContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const dispatch = useAppDispatch();

  const {
    getters: {
      activeDrawingApplianceId,
      activeDrawingAppliance,
      activeDrawingItem,
      activeDrawingShape,
      activeDrawingObject,
      actualDrawingShapes,
      areThereShapesInTheDrawingArea,
      drawingShapes,
      numberOfShapesInTheDrawingArea,
      showNotification,
      selectedSinksInDrawing,
      isCornerRadiusConstraintModalOpen,
    },
    handlers: {
      setDrawingShapes,
      updateShowNotification,
      setIsCornerRadiusConstraintModalOpen,
    },
  } = useDrawingShape();

  const updateActiveDrawingShapeId = useCallback(
    (id?: DrawingShape['id'], options?: { forceUpdate: boolean }) => {
      if (id) {
        const isDifferentShape = activeDrawingObject?.object?.id !== id;

        // If forceUpdate is true (e.g., from Sides Panel), always update and redraw
        // to ensure dimension letters are shown, matching behavior when clicking directly on shapes.
        // If forceUpdate is false (e.g., from estimate loading), only update if the ID is different
        // to prevent unnecessary updates and potential infinite loops.
        if (options?.forceUpdate || isDifferentShape) {
          dispatch(
            setActiveDrawingObject({
              type: 'shape',
              id,
            })
          );
          // Always trigger redraw when updating to ensure dimension letters are shown
          dispatch(updateNeedRedrawCanvas(true));
        }
      }
    },
    [activeDrawingObject?.object?.id, dispatch]
  );

  const updateActiveDrawingAppliance = useCallback(
    (value: string | undefined) => {
      if (value) {
        // Just set the appliance directly
        dispatch(
          setActiveDrawingObject({
            type: 'appliance',
            id: value,
          })
        );
      } else {
        // Clear the active appliance
        dispatch(resetActiveDrawingObject());
      }
    },
    [dispatch]
  );

  const resetActiveShape = () => updateActiveDrawingShapeId(undefined);

  const autoPlaceShapeTranslation = useCallback(
    (newPoly: Vec2[]): Vec2 => {
      const currentPolys = actualDrawingShapes.map(
        (shape) => shape.highlightPoints ?? []
      );

      // add any free standing appliances
      const freeStandingApplianceOutlines =
        drawingShapes[FAUX_SHAPE_INDEX].appliances?.map((appliance) =>
          getApproximateApplianceOutline(appliance)
        ) ?? [];

      return autoPlaceTranslation(newPoly, [
        ...currentPolys,
        ...freeStandingApplianceOutlines,
      ]);
    },
    [actualDrawingShapes, drawingShapes]
  );

  const resetDrawingShapes = () => {
    updateShowNotification({});
    setDrawingShapes([drawingTool.buildFauxShape()]);
  };

  useEffect(() => {
    if (!activeDrawingObject?.object) {
      return;
    }

    const objectType = activeDrawingObject.object.type;
    const objectId = activeDrawingObject.object.id;

    // Handle shape changes
    if (objectType === 'shape') {
      if (activeDrawingShape?.id !== objectId) {
        updateActiveDrawingShapeId(objectId);
        // Only trigger redraw for shape changes
        dispatch(updateNeedRedrawCanvas(true));
      }

      return; // Early return to avoid appliance logic
    }

    // Handle appliance changes - DO NOT trigger redraw
    if (objectType === 'appliance') {
      // Appliances are handled directly in mouse event handlers
      // DO NOT call updateNeedRedrawCanvas here
      return;
    }
  }, [
    activeDrawingApplianceId,
    activeDrawingObject,
    activeDrawingObject?.object,
    activeDrawingShape?.id,
    dispatch,
    updateActiveDrawingShapeId,
  ]);

  const getters: DrawingShapeContextType['getters'] = {
    activeDrawingApplianceId,
    activeDrawingAppliance,
    activeDrawingItem,
    activeDrawingObject,
    activeDrawingShape,
    actualDrawingShapes,
    areThereShapesInTheDrawingArea,
    drawingShapes,
    numberOfShapesInTheDrawingArea,
    showNotification,
    selectedSinksInDrawing,
    isCornerRadiusConstraintModalOpen,
  };

  const handlers: DrawingShapeContextType['handlers'] = {
    autoPlaceShapeTranslation,
    resetActiveShape,
    resetDrawingShapes,
    updateActiveDrawingAppliance,
    updateActiveDrawingShapeId,
    updateDrawingShapes: setDrawingShapes,
    updateShowNotification,
    setIsCornerRadiusConstraintModalOpen,
  };
  const contextValue: DrawingShapeContextType = {
    getters,
    handlers,
  };

  return (
    <DrawingShapeContext.Provider value={contextValue}>
      {children}
    </DrawingShapeContext.Provider>
  );
};
